import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import type { Transaction } from '@/types';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Get all transactions for the entire year
 */
export async function getAllYearTransactions(userId: string, year: number): Promise<Transaction[]> {
  try {
    const monthsCol = collection(db, `users/${userId}/years/${year}/months`);
    const monthsSnapshot = await getDocs(monthsCol);
    
    const allTransactions: Transaction[] = [];
    
    for (const monthDoc of monthsSnapshot.docs) {
      const transactionsCol = collection(
        db, 
        `users/${userId}/years/${year}/months/${monthDoc.id}/transactions`
      );
      const transactionsSnapshot = await getDocs(transactionsCol);
      
      transactionsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        allTransactions.push({
          id: doc.id,
          ...data,
          date: data.date?.toDate?.() || new Date(),
          createdAt: data.createdAt?.toDate?.() || new Date(),
        } as Transaction);
      });
    }
    
    // Sort by date descending
    return allTransactions.sort((a, b) => b.date.getTime() - a.date.getTime());
  } catch (error) {
    console.error('Error getting all year transactions:', error);
    return [];
  }
}

/**
 * Get monthly summary data for all 12 months
 */
export async function getYearMonthlySummary(userId: string, year: number) {
  try {
    const monthsCol = collection(db, `users/${userId}/years/${year}/months`);
    const snapshot = await getDocs(monthsCol);

    const monthlyDataMap = new Map<string, {
      salary: number;
      totalGains: number;
      totalExpenses: number;
      savings: number;
    }>();
    
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      monthlyDataMap.set(doc.id, {
        salary: data.salary || 0,
        totalGains: data.totalGains || 0,
        totalExpenses: data.totalExpenses || 0,
        savings: data.savings || 0,
      });
    });

    // Return data for all 12 months (even if 0)
    return MONTHS.map(month => ({
      month,
      salary: monthlyDataMap.get(month)?.salary || 0,
      totalGains: monthlyDataMap.get(month)?.totalGains || 0,
      totalExpenses: monthlyDataMap.get(month)?.totalExpenses || 0,
      savings: monthlyDataMap.get(month)?.savings || 0,
    }));
  } catch (error) {
    console.error('Error getting year monthly summary:', error);
    return [];
  }
}